import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, LANGS } from "@/i18n";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const base = pathname === "/" ? "" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    [`${base}#about`, t.nav.about],
    [`${base}#services`, t.nav.services],
    [`${base}#collections`, t.nav.collections],
    [`${base}#tours`, t.nav.tours],
    [`${base}#gallery`, t.nav.gallery],
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 border-b ${
        scrolled ? "bg-[#0B0C10]/85 backdrop-blur-xl gold-hairline" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
        <a href="/" data-testid="nav-logo-link" className="flex items-center gap-3 shrink-0">
          <img
            src="/assets/logo.jpg"
            alt="Explore India — Experience the Soul of India"
            className="h-12 w-12 rounded-full object-cover ring-1 ring-gold/50 shadow-[0_0_24px_rgba(212,175,55,0.25)]"
          />
          <span className="hidden sm:block leading-tight">
            <span className="block font-display text-lg font-semibold tracking-wide text-slate-100">Explore India</span>
            <span className="block text-[10px] uppercase tracking-[0.25em] text-gold/80 font-mono">exploreindians.com</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {links.map(([href, label]) => (
            <a
              key={href}
              href={href}
              data-testid={`nav-link-${href.split("#")[1]}`}
              className="text-sm text-slate-300 hover:text-gold-light transition-colors tracking-wide"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-full border border-gold/30 overflow-hidden" data-testid="language-toggle">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}-btn`}
                onClick={() => setLang(l.code)}
                className={`px-2.5 py-1.5 text-xs font-semibold tracking-wider transition-colors ${
                  lang === l.code ? "bg-gold text-obsidian" : "text-slate-300 hover:text-gold-light"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <a
            href={`${base}#enquiry`}
            data-testid="nav-book-tour-cta"
            className="hidden md:inline-flex items-center rounded-full bg-gradient-to-r from-gold to-gold-amber px-5 py-2.5 text-sm font-bold text-obsidian hover:shadow-[0_0_28px_rgba(212,175,55,0.45)] transition-shadow"
          >
            {t.nav.cta}
          </a>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-slate-200 p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0B0C10]/95 backdrop-blur-xl border-b gold-hairline overflow-hidden"
            data-testid="mobile-nav"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {links.map(([href, label]) => (
                <a
                  key={href}
                  href={href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${href.split("#")[1]}`}
                  className="text-base text-slate-200 hover:text-gold-light transition-colors"
                >
                  {label}
                </a>
              ))}
              <a
                href={`${base}#enquiry`}
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-book-cta"
                className="mt-2 inline-flex w-fit rounded-full bg-gradient-to-r from-gold to-gold-amber px-5 py-2.5 text-sm font-bold text-obsidian"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
